// zuwendungenForm.ts — die reine Umrechnung zwischen Maske und Server.
//
// BEWUSST OHNE REACT: Diese Funktionen benutzt auch `kontaktSpeicherPayload`
// und der Payload-Bau der Anlage. Lägen sie neben der Komponente, zöge jeder
// dieser Aufrufer die halbe Fluent-Oberfläche mit — im Test scheiterte das
// sofort („No makeStyles export is defined on the mock"), in der Auslieferung
// wäre es ein Bündel, das niemand bemerkt.
//
// Die Liste trägt ZWEI Ids: eine String-`id` fürs Rendern (React-Keys) und die
// Server-Id in `dbId`. Wer sie verwechselt, bekommt keinen Fehler, sondern
// Dubletten — der Abgleich hielte jede Zeile für neu.

import type { ZuwendungsArt, ZuwendungsRichtung } from "./zuwendungsarten";

export type ZuwendungItem = {
  id: string;
  dbId?: number;
  art: ZuwendungsArt;
  richtung: ZuwendungsRichtung;
  was: string;
  wann: string;
  anlass: string;
  /**
   * Nur Einladungen (seit 28.09.2026 eigene Spalten): Stand, Priorität des
   * Gastes, Datei. Fehlten sie hier, fiele die Akte beim Laden auf die
   * Vorgaben der Karte zurück und schriebe sie beim Speichern zurück.
   */
  status?: string;
  prio?: string;
  fileUrl?: string;
  fileName?: string;
};

/** Die Einladungsangaben einer Zeile — leere fallen weg. */
const einladungsAngaben = (z: any): Pick<ZuwendungItem, "status" | "prio" | "fileUrl" | "fileName"> => {
  if (z?.art !== "einladung") return {};
  const out: Pick<ZuwendungItem, "status" | "prio" | "fileUrl" | "fileName"> = {};
  for (const k of ["status", "prio", "fileUrl", "fileName"] as const) {
    const v = typeof z?.[k] === "string" ? z[k].trim() : "";
    if (v) out[k] = v;
  }
  return out;
};

/** Eigener Schlüssel fürs Rendern — ohne Abhängigkeit zur Beziehungs-Anlage. */
let zaehler = 0;
const neueId = (): string => `zw-${++zaehler}-${Math.random().toString(36).slice(2, 8)}`;

const alsTag = (v: unknown): string => String(v ?? "").slice(0, 10);

/** Liest die Liste aus einem Formularfeld — Unlesbares gilt als leer. */
export const zuwendungenAusFeld = (roh: unknown): ZuwendungItem[] => {
  const liste = Array.isArray(roh)
    ? roh
    : typeof roh === "string" && roh.trim()
      ? (() => {
          try {
            const p = JSON.parse(roh);
            return Array.isArray(p) ? p : [];
          } catch {
            return [];
          }
        })()
      : [];
  return liste.map((z: any) => ({
    id: typeof z?.id === "string" ? z.id : neueId(),
    ...(typeof z?.dbId === "number" ? { dbId: z.dbId } : {}),
    art: (z?.art ?? "geschenk") as ZuwendungsArt,
    richtung: (z?.richtung ?? "von_uns") as ZuwendungsRichtung,
    was: String(z?.was ?? ""),
    wann: alsTag(z?.wann),
    anlass: String(z?.anlass ?? ""),
    ...einladungsAngaben(z),
  }));
};

/** Aus API-Zeilen Formulareinträge machen (Akte). */
export const zuwendungenAusApi = (rows: unknown): ZuwendungItem[] =>
  Array.isArray(rows)
    ? rows.map((r: any) => ({
        id: neueId(),
        dbId: typeof r?.id === "number" ? r.id : undefined,
        art: (r?.art ?? "geschenk") as ZuwendungsArt,
        richtung: (r?.richtung ?? "von_uns") as ZuwendungsRichtung,
        was: String(r?.was ?? ""),
        wann: alsTag(r?.wann),
        anlass: String(r?.anlass ?? ""),
        ...einladungsAngaben(ausAltemAnlass(r)),
      }))
    : [];

/**
 * Einladungen von vor dem 28.09.2026 tragen Stand, Priorität und Dateiname nur
 * im `anlass` („Zugesagt · Prio B", „Einladung erhalten · Datei: x.pdf") — so
 * hat die Karte ihn geschrieben. Fehlen die eigenen Spalten, werden sie daraus
 * gelesen; sonst fiele die Karte auf ihre Vorgaben zurück und schriebe diese
 * beim nächsten Speichern fest.
 */
const ausAltemAnlass = (r: any): any => {
  if (r?.art !== "einladung" || r?.status) return r;
  const teile = String(r?.anlass ?? "")
    .split(" · ")
    .map((t) => t.trim())
    .filter(Boolean);
  if (!teile.length) return r;
  const prio = teile.map((t) => /^Prio ([ABC])$/.exec(t)?.[1]).find(Boolean);
  const datei = teile.map((t) => /^Datei: (.+)$/.exec(t)?.[1]).find(Boolean);
  const status = teile.find((t) => !/^Prio [ABC]$/.test(t) && !/^Datei: /.test(t));
  return {
    ...r,
    ...(status ? { status } : {}),
    ...(prio && !r?.prio ? { prio } : {}),
    ...(datei && !r?.fileName ? { fileName: datei } : {}),
  };
};

/**
 * Was an den Server geht.
 *
 * Zeilen ohne `was` fallen heraus: Der Server verwürfe sie ohnehin (ohne diese
 * Angabe steht da „irgendetwas, irgendwann"), und eine leer hinzugefügte Zeile
 * soll das Speichern nicht mit einer stillen Verwerfung quittieren.
 *
 * Leere Angaben werden `null`, nicht "": Der Server legt `wann` als `date` an,
 * und "" ist für Postgres kein Datum (22007) — das risse das Speichern der
 * ganzen Maske.
 */
export const zuwendungenFuerApi = (liste: ZuwendungItem[]) =>
  liste
    .filter((z) => z.was.trim())
    .map((z) => ({
      ...(z.dbId != null ? { id: z.dbId } : {}),
      art: z.art,
      richtung: z.richtung,
      was: z.was.trim(),
      wann: z.wann || null,
      anlass: z.anlass.trim() || null,
      ...(z.art === "einladung"
        ? {
            status: z.status?.trim() || null,
            prio: z.prio?.trim() || null,
            fileUrl: z.fileUrl?.trim() || null,
            fileName: z.fileName?.trim() || null,
          }
        : {}),
    }));
